/**
 * Plans & credits: /admin/billing. Loop-ADMIN-only, same posture as
 * GodView.tsx: the client-side `account.isAdmin` redirect is cosmetic, the
 * real gate is cloud-api's middleware/requireAdmin.ts, and it is not wired
 * through lib/api.ts's mock/real seam because its nav entry is hidden in mock
 * mode.
 *
 * Nothing sells a plan or takes a payment yet, so this is where both are set
 * by hand (apps/cloud/src/services/billing/):
 *
 *   - PLAN. Basic is the default and never ends. Pro is granted for a number
 *     of months; granting it again while it is still running adds the months
 *     to the current end date. Granting Basic ends a Pro grant now. After a
 *     Pro grant ends the account reads as Basic by itself; this screen still
 *     shows the old grant as "expired" so the history is visible.
 *   - CREDITS. US dollars. Paid credits never expire; promotional credits are
 *     free and must have an end date, and will be spent first. A grant made by
 *     mistake is revoked, never deleted.
 *
 * Nothing on this screen is enforced anywhere yet: it changes what the
 * account's Usage screen says and nothing else.
 */
import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Navigate } from 'react-router';

import { Button } from '../../components/primitives/Button';
import { Dialog } from '../../components/primitives/Dialog';
import { Field } from '../../components/primitives/Field';
import { Select } from '../../components/primitives/Select';
import { showToast } from '../../components/primitives/Toast';
import { Table, type TableColumn } from '../../components/primitives/Table';
import { EmptyState } from '../../components/patterns/EmptyState';
import { Notice } from '../../components/patterns/Notice';
import { SectionPanel } from '../../components/patterns/SectionPanel';
import { Skeleton } from '../../components/patterns/Skeleton';
import { dateLabel } from '../../components/patterns/DeployRow';

import { useAccount } from '../../lib/auth/use-app-data';
import { useAsync } from '../../lib/hooks/use-async';
import { apiFetch, ApiError } from '../../lib/http';
import { t } from '../../lib/i18n/t';
import { captureEvent } from '../../lib/analytics/posthog';
import { useFormTracker } from '../../lib/analytics/use-form-tracker';
import type { Plan } from '../../lib/types';

import '../../styles/screens/admin.css';

interface AccountRowWire {
  id: string;
  email: string;
  display_name: string | null;
  plan_id: Plan;
  plan_expires_at: string | null;
  created_at: string;
}

interface AccountListWire {
  accounts: AccountRowWire[];
  truncated: boolean;
}

interface GrantWire {
  id: string;
  kind: 'paid' | 'promotional';
  amount_usd: string;
  remaining_usd: string;
  expires_at: string | null;
  expired: boolean;
  note: string | null;
  created_at: string;
  revoked_at: string | null;
}

interface BillingWire {
  account_id: string;
  email: string;
  plan_id: Plan;
  stored_plan_id: string;
  plan_since: string | null;
  plan_expires_at: string | null;
  plan_expired: boolean;
  credits: { paid_usd: string; promotional_usd: string; promotional_next_expiry: string | null };
  grants: GrantWire[];
  /** Closed cycles, newest first (apps/cloud/src/services/billing/statements.ts). */
  statements?: StatementWire[];
  /** Billing cycle and subscription (2026-10-13). */
  plan_anchor_at: string | null;
  cycle_start: string;
  cycle_end: string;
  plan_standing: 'trial' | 'active' | 'lapsed';
  credits_pay_plan: boolean;
  pending_plan_id: Plan | null;
  lapsed_at: string | null;
  plan_charges: ChargeWire[];
}

interface ChargeWire {
  id: string;
  kind: 'trial' | 'renewal' | 'restart' | 'proration' | 'grant';
  plan_id: Plan;
  cycle_start: string;
  cycle_end: string;
  amount_usd: string;
  created_at: string;
}

interface QuoteWire {
  plan_id: Plan;
  applies: 'now' | 'next_cycle';
  amount_usd: string;
}

interface StatementWire {
  id: string;
  period_start: string;
  plan_id: Plan;
  overage_usd: string;
  overage_rate_usd_per_million: string | null;
  credits_applied_usd: string;
  unpaid_usd: string;
  closed_at: string;
}

/** "September 2026", in the reader's language, from 'YYYY-MM-DD'. */
function monthLabel(periodStart: string): string {
  return new Date(`${periodStart}T00:00:00Z`).toLocaleDateString(undefined, {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

const PLAN_NAMES: Record<Plan, string> = {
  basic: 'Basic',
  pro: 'Pro',
  max: 'Max',
};

const MAX_MONTHS = 36;

async function searchAccounts(q: string): Promise<AccountListWire> {
  const query = q.trim() ? `&q=${encodeURIComponent(q.trim())}` : '';
  return apiFetch<AccountListWire>(`/api/v1/admin/accounts?status=active${query}`);
}

async function loadBilling(id: string | null): Promise<BillingWire | null> {
  if (!id) return null;
  return apiFetch<BillingWire>(`/api/v1/admin/accounts/${id}/billing`);
}

/**
 * The server's cycle rule (apps/cloud/src/services/billing/cycle.ts), for the
 * preview only: `n` months after `anchor`, clamped to a short month's last
 * day and back to the anchor day after. The server's answer is the one saved.
 */
function boundaryUtc(anchor: Date, n: number): Date {
  const total = anchor.getUTCMonth() + n;
  const year = anchor.getUTCFullYear() + Math.floor(total / 12);
  const month = ((total % 12) + 12) % 12;
  const last = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  return new Date(
    Date.UTC(year, month, Math.min(anchor.getUTCDate(), last), anchor.getUTCHours(), anchor.getUTCMinutes(), anchor.getUTCSeconds()),
  );
}

/** Whole months from `anchor` to `end`, which is a cycle boundary. */
function monthsBetween(anchor: Date, end: Date): number {
  return (end.getUTCFullYear() - anchor.getUTCFullYear()) * 12 + (end.getUTCMonth() - anchor.getUTCMonth());
}

const STANDING_LABELS: Record<BillingWire['plan_standing'], string> = {
  trial: 'Trial',
  active: 'Paid',
  lapsed: 'Ended',
};

const CHARGE_LABELS: Record<ChargeWire['kind'], string> = {
  trial: 'Trial',
  renewal: 'Renewal',
  restart: 'Restart',
  proration: 'Upgrade',
  grant: 'Free months',
};

function planReading(b: Pick<BillingWire, 'plan_id' | 'stored_plan_id' | 'plan_expires_at' | 'plan_expired'>): string {
  if (b.plan_expired && b.plan_expires_at) {
    return t('Basic. {plan} ended {date}', {
      plan: t(PLAN_NAMES[b.stored_plan_id as Plan] ?? b.stored_plan_id),
      date: dateLabel(b.plan_expires_at),
    });
  }
  if (b.plan_expires_at) {
    return t('{plan}, until {date}', { plan: t(PLAN_NAMES[b.plan_id]), date: dateLabel(b.plan_expires_at) });
  }
  return t(PLAN_NAMES[b.plan_id]);
}

function errorMessage(err: unknown): string {
  return err instanceof ApiError ? err.message : t('Something went wrong. Try again in a moment.');
}

export default function Billing() {
  const account = useAccount();

  const [query, setQuery] = useState('');
  const [submittedQuery, setSubmittedQuery] = useState('');
  const { data: list, isLoading: listLoading, error: listError, refetch: refetchList } = useAsync(
    () => searchAccounts(submittedQuery),
    [submittedQuery],
  );

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const { data: billing, isLoading: billingLoading, error: billingError, refetch: refetchBilling } =
    useAsync(() => loadBilling(selectedId), [selectedId]);

  // Grant plan form.
  const [planId, setPlanId] = useState<Plan>('pro');
  const [months, setMonths] = useState('1');
  const [granting, setGranting] = useState(false);
  const planForm = useFormTracker({ formId: 'admin_grant_plan', formName: 'Grant plan', totalFields: 2 });

  // Change plan form (paid, 2026-10-13).
  const [changeId, setChangeId] = useState<Plan>('max');
  const [changing, setChanging] = useState(false);
  const changeForm = useFormTracker({ formId: 'admin_change_plan', formName: 'Change plan', totalFields: 1 });
  const { data: quote } = useAsync(
    () =>
      billing && changeId !== billing.plan_id && !billing.plan_expired
        ? apiFetch<QuoteWire>(`/api/v1/admin/accounts/${billing.account_id}/plan-change?plan_id=${changeId}`)
        : Promise.resolve(null),
    [billing?.account_id, billing?.plan_id, changeId],
  );

  // Add credits form.
  const [kind, setKind] = useState<'paid' | 'promotional'>('promotional');
  const [amount, setAmount] = useState('');
  const [expiresOn, setExpiresOn] = useState('');
  const [note, setNote] = useState('');
  const [adding, setAdding] = useState(false);
  const creditForm = useFormTracker({ formId: 'admin_add_credits', formName: 'Add credits', totalFields: 4 });

  const [revokeTarget, setRevokeTarget] = useState<GrantWire | null>(null);
  const [revoking, setRevoking] = useState(false);

  useEffect(() => {
    if (account.isAdmin) captureEvent('admin_billing_viewed');
  }, [account.isAdmin]);

  useEffect(() => {
    if (account.isAdmin && billing) {
      captureEvent('admin_billing_account_opened', {
        target_account_id: billing.account_id,
        plan: billing.plan_id,
        grant_count: billing.grants.length,
      });
    }
    // Keyed on the account id: once per opened account, not on every refetch after a change.
  }, [account.isAdmin, billing?.account_id]);

  const monthsNumber = Number(months);
  const monthsValid = Number.isInteger(monthsNumber) && monthsNumber >= 1 && monthsNumber <= MAX_MONTHS;

  // What the grant would produce, worked out the same way the server will.
  const preview = useMemo(() => {
    if (!billing) return '';
    if (!monthsValid) return '';
    // Only a grant of the SAME plan extends, on its existing anchor; anything
    // else starts a new cycle today.
    const extending = billing.plan_id === planId && billing.plan_expires_at && !billing.plan_expired;
    const anchor = extending && billing.plan_anchor_at ? new Date(billing.plan_anchor_at) : new Date();
    const already = extending && billing.plan_expires_at ? monthsBetween(anchor, new Date(billing.plan_expires_at)) : 0;
    const until = dateLabel(boundaryUtc(anchor, already + monthsNumber).toISOString());
    const plan = t(PLAN_NAMES[planId]);
    return extending
      ? t('{plan}, extended until {date}.', { plan, date: until })
      : t('{plan}, until {date}.', { plan, date: until });
  }, [billing, planId, monthsValid, monthsNumber]);

  if (!account.isAdmin) {
    return <Navigate to="/projects" replace />;
  }

  function submitSearch(e: FormEvent) {
    e.preventDefault();
    setSubmittedQuery(query);
    captureEvent('admin_billing_search', { query_length: query.trim().length });
  }

  function openAccount(id: string) {
    setSelectedId(id);
    setPlanId('pro');
    setMonths('1');
  }

  async function changeToPlan(e: FormEvent) {
    e.preventDefault();
    if (!billing) return;
    setChanging(true);
    changeForm.trackSubmit({ plan: changeId, applies: quote?.applies ?? null });
    try {
      const res = await apiFetch<BillingWire>(`/api/v1/admin/accounts/${billing.account_id}/plan-change`, {
        method: 'POST',
        body: { plan_id: changeId },
      });
      changeForm.trackSubmitSuccess({ target_account_id: billing.account_id, plan: changeId });
      showToast(
        res.pending_plan_id
          ? t('{email} moves to {plan} at the next cycle.', { email: billing.email, plan: t(PLAN_NAMES[changeId]) })
          : t('{email} is now on {reading}.', { email: billing.email, reading: planReading(res) }),
      );
      refetchBilling();
      refetchList();
    } catch (err) {
      const message = errorMessage(err);
      changeForm.trackSubmitError(err instanceof ApiError ? err.code : 'unknown', message);
      showToast(message);
    } finally {
      setChanging(false);
    }
  }

  async function grantPlan(e: FormEvent) {
    e.preventDefault();
    if (!billing) return;
    // Every plan, Basic included, is granted for a number of months (2026-10-13).
    if (!monthsValid) {
      const message = t('Months must be a whole number from 1 to {n}.', { n: MAX_MONTHS });
      planForm.onValidationError('months', message);
      showToast(message);
      return;
    }
    setGranting(true);
    planForm.trackSubmit({ plan: planId, months: monthsNumber });
    try {
      const res = await apiFetch<BillingWire>(`/api/v1/admin/accounts/${billing.account_id}/plan`, {
        method: 'POST',
        body: { plan_id: planId, months: monthsNumber },
      });
      planForm.trackSubmitSuccess({ target_account_id: billing.account_id, plan: res.plan_id });
      showToast(t('{email} is now on {reading}.', { email: billing.email, reading: planReading(res) }));
      refetchBilling();
      refetchList();
    } catch (err) {
      const message = errorMessage(err);
      planForm.trackSubmitError(err instanceof ApiError ? err.code : 'unknown', message);
      showToast(message);
    } finally {
      setGranting(false);
    }
  }

  async function addCredits(e: FormEvent) {
    e.preventDefault();
    if (!billing) return;
    if (kind === 'promotional' && !expiresOn) {
      const message = t('Promotional credits need an end date.');
      creditForm.onValidationError('expires_on', message);
      showToast(message);
      return;
    }
    setAdding(true);
    creditForm.trackSubmit({ kind, has_note: note.trim().length > 0 });
    try {
      await apiFetch<BillingWire>(`/api/v1/admin/accounts/${billing.account_id}/credits`, {
        method: 'POST',
        body: {
          kind,
          amount_usd: amount.trim(),
          // The end of the chosen day in the admin's own time zone, so "ends 30 November" means
          // the whole of that day and reads back as 30 November rather than the day after.
          expires_at: kind === 'promotional' ? new Date(`${expiresOn}T23:59:59.999`).toISOString() : null,
          note: note.trim() || null,
        },
      });
      creditForm.trackSubmitSuccess({ target_account_id: billing.account_id, kind });
      showToast(t('Added {amount} in credits to {email}.', { amount: `$${amount.trim()}`, email: billing.email }));
      setAmount('');
      setNote('');
      setExpiresOn('');
      refetchBilling();
    } catch (err) {
      const message = errorMessage(err);
      creditForm.trackSubmitError(err instanceof ApiError ? err.code : 'unknown', message);
      showToast(message);
    } finally {
      setAdding(false);
    }
  }

  async function confirmRevoke() {
    if (!revokeTarget) return;
    setRevoking(true);
    captureEvent('admin_credit_grant_revoked', { grant_id: revokeTarget.id, kind: revokeTarget.kind });
    try {
      await apiFetch<BillingWire>(`/api/v1/admin/credit-grants/${revokeTarget.id}/revoke`, { method: 'POST' });
      showToast(t('Credits revoked.'));
      setRevokeTarget(null);
      refetchBilling();
    } catch (err) {
      showToast(errorMessage(err));
    } finally {
      setRevoking(false);
    }
  }

  const accountColumns: TableColumn<AccountRowWire>[] = [
    { key: 'email', header: t('Email'), render: (row) => row.email },
    {
      key: 'plan',
      header: t('Plan'),
      render: (row) =>
        row.plan_expires_at
          ? t('{plan}, until {date}', { plan: t(PLAN_NAMES[row.plan_id]), date: dateLabel(row.plan_expires_at) })
          : t(PLAN_NAMES[row.plan_id]),
    },
    { key: 'joined', header: t('Joined'), render: (row) => dateLabel(row.created_at) },
    {
      key: 'actions',
      header: t('Actions'),
      align: 'end',
      render: (row) => (
        <div className="admin__actions">
          <Button
            size="sm"
            variant={selectedId === row.id ? 'solid' : 'outline'}
            onClick={() => openAccount(row.id)}
          >
            {t('Manage')}
          </Button>
        </div>
      ),
    },
  ];

  const grantColumns: TableColumn<GrantWire>[] = [
    {
      key: 'kind',
      header: t('Kind'),
      render: (row) => (row.kind === 'paid' ? t('Paid') : t('Promotional')),
    },
    { key: 'amount', header: t('Amount'), render: (row) => <span className="mono">${row.amount_usd}</span> },
    { key: 'left', header: t('Left'), render: (row) => <span className="mono">${row.remaining_usd}</span> },
    {
      key: 'expires',
      header: t('Ends'),
      render: (row) => (row.expires_at ? dateLabel(row.expires_at) : t('Never')),
    },
    { key: 'added', header: t('Added'), render: (row) => dateLabel(row.created_at) },
    { key: 'note', header: t('Note'), render: (row) => row.note ?? '' },
    {
      key: 'status',
      header: t('Status'),
      render: (row) =>
        row.revoked_at
          ? t('Revoked {date}', { date: dateLabel(row.revoked_at) })
          : row.expired
            ? t('Expired')
            : t('Available'),
    },
    {
      key: 'actions',
      header: t('Actions'),
      align: 'end',
      render: (row) =>
        row.revoked_at ? null : (
          <div className="admin__actions">
            <Button size="sm" variant="danger-outline" onClick={() => setRevokeTarget(row)}>
              {t('Revoke')}
            </Button>
          </div>
        ),
    },
  ];

  const chargeColumns: TableColumn<ChargeWire>[] = [
    { key: 'kind', header: t('Kind'), render: (row) => t(CHARGE_LABELS[row.kind]) },
    { key: 'plan', header: t('Plan'), render: (row) => t(PLAN_NAMES[row.plan_id] ?? row.plan_id) },
    {
      key: 'cycle',
      header: t('Cycle'),
      render: (row) => t('{start} to {end}', { start: dateLabel(row.cycle_start), end: dateLabel(row.cycle_end) }),
    },
    {
      key: 'amount',
      header: t('Amount'),
      align: 'end',
      render: (row) => <span className="mono">${row.amount_usd}</span>,
    },
  ];

  const statementColumns: TableColumn<StatementWire>[] = [
    { key: 'month', header: t('Month'), render: (row) => monthLabel(row.period_start) },
    { key: 'plan', header: t('Plan'), render: (row) => t(PLAN_NAMES[row.plan_id] ?? row.plan_id) },
    {
      key: 'overage',
      header: t('Past allowance'),
      render: (row) => <span className="mono">${row.overage_usd}</span>,
    },
    {
      key: 'paid',
      header: t('From credits'),
      render: (row) => <span className="mono">${row.credits_applied_usd}</span>,
    },
    {
      key: 'unpaid',
      header: t('Not covered'),
      align: 'end',
      render: (row) => <span className="mono">${row.unpaid_usd}</span>,
    },
  ];

  return (
    <div className="page">
      <header className="page__head">
        <h1 className="page__title">{t('Plans & credits')}</h1>
        <p className="page__lede">
          {t('Set an account plan and add credits by hand. Compute past the allowance is paid from credits when each month closes; nothing is limited.')}
        </p>
      </header>

      <SectionPanel title={t('Accounts')}>
        <form className="admin__search" onSubmit={submitSearch}>
          <input
            type="text"
            className="admin__search-input"
            placeholder={t('Search accounts by email...')}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label={t('Search accounts by email')}
          />
          <Button type="submit" size="sm" variant="outline">
            {t('Search')}
          </Button>
        </form>

        {listLoading && <Skeleton shape="row" count={4} label={t('Loading accounts')} />}
        {listError && <Notice tone="attention">{t('Could not load accounts. Try again in a moment.')}</Notice>}
        {list && list.accounts.length === 0 && (
          <EmptyState mark="box" sentence={t('No accounts match that search.')} />
        )}
        {list && list.accounts.length > 0 && (
          <Table caption={t('Accounts')} columns={accountColumns} rows={list.accounts} rowKey={(row) => row.id} />
        )}
        {list?.truncated && <p className="page__lede">{t('Showing the most recent accounts only.')}</p>}
      </SectionPanel>

      {selectedId && billingLoading && !billing && (
        <Skeleton shape="row" count={3} label={t('Loading plan and credits')} />
      )}
      {selectedId && billingError && (
        <Notice tone="attention">{t('Could not load this account. Try again in a moment.')}</Notice>
      )}

      {billing && (
        <>
          <SectionPanel title={billing.email} note={planReading(billing)}>
            <dl className="admin__facts">
              <div>
                <dt>{t('Credits')}</dt>
                <dd className="mono">${billing.credits.paid_usd}</dd>
              </div>
              <div>
                <dt>{t('Promotional credits')}</dt>
                <dd className="mono">${billing.credits.promotional_usd}</dd>
              </div>
              {billing.credits.promotional_next_expiry ? (
                <div>
                  <dt>{t('Next promotional end date')}</dt>
                  <dd>{dateLabel(billing.credits.promotional_next_expiry)}</dd>
                </div>
              ) : null}
              <div>
                <dt>{t('Standing')}</dt>
                <dd>{t(STANDING_LABELS[billing.plan_standing])}</dd>
              </div>
              <div>
                <dt>{t('This cycle')}</dt>
                <dd>{t('{start} to {end}', { start: dateLabel(billing.cycle_start), end: dateLabel(billing.cycle_end) })}</dd>
              </div>
              <div>
                <dt>{t('Credits pay the plan')}</dt>
                <dd>{billing.credits_pay_plan ? t('Yes') : t('No, turned off by the owner')}</dd>
              </div>
              {billing.pending_plan_id ? (
                <div>
                  <dt>{t('Next cycle')}</dt>
                  <dd>{t('Moves to {plan}', { plan: t(PLAN_NAMES[billing.pending_plan_id]) })}</dd>
                </div>
              ) : null}
            </dl>
          </SectionPanel>

          <SectionPanel
            title={t('Change plan')}
            note={t('Paid from the account\'s credits. An upgrade applies now and charges the difference for the rest of this cycle; a downgrade applies at the next cycle. No refunds.')}
          >
            <form className="admin__form" onSubmit={changeToPlan}>
              <Select
                label={t('New plan')}
                value={changeId}
                options={[
                  { value: 'basic', label: t('Basic') },
                  { value: 'pro', label: t('Pro') },
                  { value: 'max', label: t('Max') },
                ]}
                onFocus={() => changeForm.onFieldFocus('plan')}
                onBlur={() => changeForm.onFieldBlur('plan')}
                onChange={(e) => {
                  setChangeId(e.target.value as Plan);
                  changeForm.onFieldChange('plan', e.target.value);
                }}
              />
              {quote && changeId !== billing.plan_id ? (
                <p className="admin__preview">
                  {quote.applies === 'now'
                    ? t('Charges {amount} from credits now.', { amount: `$${quote.amount_usd}` })
                    : t('Moves at the next cycle, {date}. No refund.', { date: dateLabel(billing.cycle_end) })}
                </p>
              ) : null}
              <div>
                <Button type="submit" variant="solid" loading={changing} disabled={changeId === billing.plan_id || billing.plan_expired}>
                  {t('Change plan')}
                </Button>
              </div>
            </form>
          </SectionPanel>

          <SectionPanel
            title={t('Grant free months')}
            note={t('Free, for a trial, a comp, or a payment taken outside SV Cloud. Granting the same plan again while it is running adds the months to its current end date; any other plan starts a new cycle today.')}
          >
            <form className="admin__form" onSubmit={grantPlan}>
              <Select
                label={t('Plan')}
                value={planId}
                options={[
                  { value: 'basic', label: t('Basic') },
                  { value: 'pro', label: t('Pro') },
                  { value: 'max', label: t('Max') },
                ]}
                onFocus={() => planForm.onFieldFocus('plan')}
                onBlur={() => planForm.onFieldBlur('plan')}
                onChange={(e) => {
                  setPlanId(e.target.value as Plan);
                  planForm.onFieldChange('plan', e.target.value);
                }}
              />
              <Field
                label={t('Months')}
                type="number"
                min={1}
                max={MAX_MONTHS}
                step={1}
                value={months}
                onFocus={() => planForm.onFieldFocus('months')}
                onBlur={() => planForm.onFieldBlur('months')}
                onChange={(e) => {
                  setMonths(e.target.value);
                  planForm.onFieldChange('months', e.target.value);
                }}
              />
              {preview && <p className="admin__preview">{preview}</p>}
              <div>
                <Button type="submit" variant="solid" loading={granting}>
                  {t('Set plan')}
                </Button>
              </div>
            </form>
          </SectionPanel>

          <SectionPanel
            title={t('Add credits')}
            note={t('Promotional credits are free, end on a date, and will be used before paid credits.')}
          >
            <form className="admin__form" onSubmit={addCredits}>
              <Select
                label={t('Kind')}
                value={kind}
                options={[
                  { value: 'promotional', label: t('Promotional') },
                  { value: 'paid', label: t('Paid') },
                ]}
                onFocus={() => creditForm.onFieldFocus('kind')}
                onBlur={() => creditForm.onFieldBlur('kind')}
                onChange={(e) => {
                  setKind(e.target.value as 'paid' | 'promotional');
                  creditForm.onFieldChange('kind', e.target.value);
                }}
              />
              <Field
                label={t('Amount in US dollars')}
                inputMode="decimal"
                placeholder="10.00"
                mono
                required
                value={amount}
                onFocus={() => creditForm.onFieldFocus('amount')}
                onBlur={() => creditForm.onFieldBlur('amount')}
                onChange={(e) => {
                  setAmount(e.target.value);
                  creditForm.onFieldChange('amount', e.target.value);
                }}
              />
              {kind === 'promotional' && (
                <Field
                  label={t('Ends on')}
                  help={t('They can be used until the end of this day.')}
                  type="date"
                  required
                  value={expiresOn}
                  onFocus={() => creditForm.onFieldFocus('expires_on')}
                  onBlur={() => creditForm.onFieldBlur('expires_on')}
                  onChange={(e) => {
                    setExpiresOn(e.target.value);
                    creditForm.onFieldChange('expires_on', e.target.value);
                  }}
                />
              )}
              <Field
                label={t('Note')}
                help={t('Only admins see this.')}
                maxLength={500}
                value={note}
                onFocus={() => creditForm.onFieldFocus('note')}
                onBlur={() => creditForm.onFieldBlur('note')}
                onChange={(e) => {
                  setNote(e.target.value);
                  creditForm.onFieldChange('note', e.target.value);
                }}
              />
              <div>
                <Button type="submit" variant="solid" loading={adding}>
                  {t('Add credits')}
                </Button>
              </div>
            </form>
          </SectionPanel>

          <SectionPanel title={t('Credit history')}>
            {billing.grants.length === 0 ? (
              <EmptyState mark="box" sentence={t('No credits added yet.')} />
            ) : (
              <Table
                caption={t('Credit history')}
                columns={grantColumns}
                rows={billing.grants}
                rowKey={(row) => row.id}
              />
            )}
          </SectionPanel>

          <SectionPanel title={t('Plan charges')}>
            {billing.plan_charges.length === 0 ? (
              <EmptyState mark="box" sentence={t('No plan charges yet.')} />
            ) : (
              <Table
                caption={t('Plan charges')}
                columns={chargeColumns}
                rows={billing.plan_charges}
                rowKey={(row) => row.id}
              />
            )}
          </SectionPanel>

          <SectionPanel
            title={t('Monthly statements')}
            note={t('Closed on the 2nd of each month. Compute only; storage past the allowance is not charged yet.')}
          >
            {!billing.statements || billing.statements.length === 0 ? (
              <EmptyState mark="box" sentence={t('No months closed yet.')} />
            ) : (
              <Table
                caption={t('Monthly statements')}
                columns={statementColumns}
                rows={billing.statements}
                rowKey={(row) => row.id}
              />
            )}
          </SectionPanel>
        </>
      )}

      <Dialog
        open={revokeTarget !== null}
        onClose={() => (revoking ? undefined : setRevokeTarget(null))}
        title={t('Revoke {amount} of credits?', { amount: `$${revokeTarget?.amount_usd ?? ''}` })}
        description={t('They stop counting toward the balance. The record stays in the history.')}
        footer={
          <>
            <Button type="button" variant="ghost" onClick={() => setRevokeTarget(null)} disabled={revoking}>
              {t('Cancel')}
            </Button>
            <Button type="button" variant="danger-outline" loading={revoking} onClick={confirmRevoke}>
              {t('Revoke')}
            </Button>
          </>
        }
      />
    </div>
  );
}
